/**
 * AdminNewsletterPage — Subscriber inbox.
 *
 * /admin/newsletter
 *   - List + search subscribers
 *   - CSV export
 *   - Delete subscriber
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Search, Trash2, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

export default function AdminNewsletterPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading, page]); // eslint-disable-line

    // Arama değişince sayfa 1'e dön
    useEffect(() => { setPage(1); }, [q]);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            params.set("limit", String(PAGE_SIZE));
            params.set("skip", String((page - 1) * PAGE_SIZE));
            const r = await api.get(`/admin/newsletter/subscribers?${params.toString()}`);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        } finally { setLoading(false); }
    };

    const handleDelete = async (id, email) => {
        if (!window.confirm(`${email} aboneliğini silmek istediğinize emin misiniz?`)) return;
        await api.delete(`/admin/newsletter/subscribers/${id}`);
        load();
    };

    const exportCsv = () => {
        const lines = ["email,name,source,subscribed_at"];
        items.forEach((s) => {
            const safe = (v) => `"${(v || "").replace(/"/g, '""')}"`;
            lines.push([safe(s.email), safe(s.name), safe(s.source), safe(s.subscribed_at)].join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <AdminLayout
            title="Newsletter Aboneleri"
            action={
                items.length > 0 ? (
                    <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">
                        <Download className="h-4 w-4" /> CSV İndir
                    </button>
                ) : null
            }
        >
            <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input data-testid="search-subscribers" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="E-posta ara..." className="w-full pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm" />
                </div>
                <span className="text-xs text-slate-500 ml-auto">{total} abone</span>
            </div>

            {loading ? <div>Yükleniyor...</div> : items.length === 0 ? (
                <div className="p-10 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
                    <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 text-sm">Henüz hiç abone yok. Footer'a abonelik formu eklediğinizde ziyaretçiler burada görünecek.</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-sm" data-testid="subscribers-table">
                        <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">E-posta</th>
                                <th className="px-4 py-3 text-left">İsim</th>
                                <th className="px-4 py-3 text-center">Kaynak</th>
                                <th className="px-4 py-3 text-center">Abone Tarihi</th>
                                <th className="px-4 py-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800/50">
                                    <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{s.name || "-"}</td>
                                    <td className="px-4 py-3 text-center"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700">{s.source}</span></td>
                                    <td className="px-4 py-3 text-center text-xs text-slate-500">{s.subscribed_at?.slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => handleDelete(s.id, s.email)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="abone" />
                </div>
            )}
        </AdminLayout>
    );
}
